import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import classNames from 'classnames';
import { useExecuteRequest } from '@core/context/useExecuteRequest';
import ProfileImage from '../other/ProfileImage';
import { Dropdown } from '@widget/popups/Dropdown';
import { ObjectInfoCard, isObjectInfoCardProps } from '@widget/list/Tables/object-search/ObjectInfoCardComponent';
import { extractFirstItem } from '@utils';
import { getFontAwesomeIcon, isFontawesomeIcon } from '@utils';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
function isExecuteRequestConfig(data) {
    return data && typeof data === 'object' && 'executeConfig' in data;
}
const DUMMY_EXECUTE_CONFIG = { type: 'query', params: { key: null } };
const DUMMY_EXECUTE_OPTIONS = { autoExecute: false };
/**
 * @export
 * Renders a data item as a horizontal identity card with avatar, title, subtitle, extra fields, badges, and actions.
 * Supports both data-fetching (via executeConfig) and pre-fetched data.
 * Use `layout="collapsed"` for the compact details-panel sidebar variant (image on right).
 *
 * @example Users row
 * ```tsx
 * <InfoCard
 *   data={user}
 *   fields={{ image: 'avatar', name: 'fullName', title: 'fullName', subtitle: 'email' }}
 *   badges={<Chip label="Active" variant="success" />}
 * />
 * ```
 *
 * @example Locations row with extra fields
 * ```tsx
 * <InfoCard
 *   data={location}
 *   fields={{ image: 'thumbnail', title: 'name', subtitle: 'type' }}
 *   extraFields={[{ value: location.address, style: 'muted' }]}
 *   badges={<Chip label={location.status} variant="success" />}
 * />
 * ```
 *
 * @example Collapsed sidebar variant
 * ```tsx
 * <InfoCard
 *   data={location}
 *   fields={{ image: 'thumbnail', name: 'name', title: 'name', subtitle: 'type' }}
 *   badges={<Chip label="Active" variant="success" />}
 *   layout="collapsed"
 * />
 * ```
 */
const InfoCard = (props) => {
    const { data, fields = {}, extraFields, details, variant = 'card', size, shape, dropdownPosition = 'bottom-left', layout = 'default', titleStyle = 'primary', onTitleClick, subtitleStyle = 'secondary', badges, actions, className, style, } = props;
    const isRequestConfig = isExecuteRequestConfig(data);
    const { executeConfig, executeOptions } = useMemo(() => ({
        executeConfig: isRequestConfig ? data === null || data === void 0 ? void 0 : data.executeConfig : DUMMY_EXECUTE_CONFIG,
        executeOptions: isRequestConfig ? Object.assign(Object.assign({}, data.executeOptions), { autoExecute: true }) : DUMMY_EXECUTE_OPTIONS,
    }), [isRequestConfig, data === null || data === void 0 ? void 0 : data.executeOptions]);
    const fetchResult = useExecuteRequest(executeConfig, executeOptions);
    const loading = isRequestConfig ? fetchResult.loading : false;
    const error = isRequestConfig ? fetchResult.error : null;
    const rawData = isRequestConfig ? fetchResult.data : data;
    const item = useMemo(() => extractFirstItem(rawData), [rawData]);
    if (loading) {
        return (_jsx("div", { className: classNames('uxpcore_infocard', 'uxpcore_infocard--loading', className), style: style, children: _jsxs("div", { className: "uxpcore_infocard__layout", children: [_jsx(ProfileImage, { size: size, shape: shape }), _jsx("div", { className: "uxpcore_infocard__body uxpcore_infocard__body--skeleton" })] }) }));
    }
    if (error || !item) {
        return (_jsx("div", { className: classNames('uxpcore_infocard', className), style: style, children: _jsx("div", { className: "uxpcore_infocard__layout", children: variant === 'avatar' ? _jsx(ProfileImage, { size: size, shape: shape }) : (_jsxs(_Fragment, { children: [_jsx(ProfileImage, { size: size, shape: shape }), _jsx("div", { className: "uxpcore_infocard__body" })] })) }) }));
    }
    const imageVal = (fields === null || fields === void 0 ? void 0 : fields.image) ? item[fields.image] : undefined;
    const nameVal = (fields === null || fields === void 0 ? void 0 : fields.name) ? item[fields.name] : undefined;
    const titleVal = (fields === null || fields === void 0 ? void 0 : fields.title) ? item[fields.title] : undefined;
    const subtitleVal = (fields === null || fields === void 0 ? void 0 : fields.subtitle) ? item[fields.subtitle] : undefined;
    if (variant === 'avatar') {
        const avatarContent = (_jsx("div", { className: classNames('uxpcore_infocard', className), style: style, children: _jsx(ProfileImage, { image: imageVal, name: nameVal, size: size, shape: shape }) }));
        if (!details)
            return avatarContent;
        return (_jsx(Dropdown, { trigger: avatarContent, content: _jsx("div", { className: "uxpcore_infocard__details", children: renderDetails(details, item) }), position: dropdownPosition }));
    }
    const avatarEl = (imageVal || nameVal) ? (_jsx("div", { className: "uxpcore_infocard__avatar", children: _jsx(ProfileImage, { image: imageVal, name: nameVal, shape: shape, size: size }) })) : null;
    const bodyEl = (_jsxs("div", { className: "uxpcore_infocard__body", children: [titleVal && (_jsx("div", { className: classNames('uxpcore_infocard__field', `uxpcore_infocard__field--${titleStyle}`, { 'uxpcore_infocard__field--clickable': !!onTitleClick }), onClick: onTitleClick, children: titleVal })), subtitleVal && (_jsx("div", { className: `uxpcore_infocard__field uxpcore_infocard__field--${subtitleStyle}`, children: subtitleVal })), extraFields === null || extraFields === void 0 ? void 0 : extraFields.map((f, i) => {
                var _a;
                return (_jsxs("div", { className: classNames('uxpcore_infocard__field', `uxpcore_infocard__field--${(_a = f.style) !== null && _a !== void 0 ? _a : 'secondary'}`, { 'uxpcore_infocard__field--clickable': !!f.onClick }), onClick: f.onClick, children: [f.icon && isFontawesomeIcon(f.icon.trim()) && (_jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon(f.icon.trim()) })), f.value] }, i));
            }), layout === 'collapsed' && badges && (_jsx("div", { className: "uxpcore_infocard__badges uxpcore_infocard__badges--inline", children: badges }))] }));
    const sideEl = (badges || actions) && layout !== 'collapsed' ? (_jsxs("div", { className: "uxpcore_infocard__side", children: [badges && _jsx("div", { className: "uxpcore_infocard__badges", children: badges }), actions && _jsx("div", { className: "uxpcore_infocard__actions", children: actions })] })) : null;
    const content = (_jsx("div", { className: classNames('uxpcore_infocard', `uxpcore_infocard--${layout}`, className), style: style, children: _jsxs("div", { className: "uxpcore_infocard__layout", children: [avatarEl, bodyEl, sideEl] }) }));
    if (!details)
        return content;
    return (_jsx(Dropdown, { trigger: content, content: _jsx("div", { className: "uxpcore_infocard__details", children: renderDetails(details, item) }), position: dropdownPosition }));
};
function renderDetails(details, item) {
    if (typeof details === 'function') {
        const result = details(item);
        if (isObjectInfoCardProps(result)) {
            return _jsx(ObjectInfoCard, Object.assign({}, result, { layout: "vertical" }));
        }
        return result;
    }
    return details;
}
export default InfoCard;
